import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  CheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { Skill, useSkills } from '../../../onboarding/apis/skill-registry';
import {
  useBrowserExtensionDetected,
  useWorkspaces,
  WorkspaceType,
} from '../../../onboarding/apis/workspace-registry';
import { skillRegistry } from '../../../onboarding/registry/SkillRegistry';
import { workspaceRegistry } from '../../../onboarding/registry/WorkspaceRegistry';
import { useUserProfile } from '../../../../api/profile';
import {
  SkillConfig,
  WorkspaceConfig,
} from '../../../onboarding/types/onboarding';
import { Image } from '../../../ui/Image';
import type { ImageProps } from '@plerionhq/design-system';
import Badge from '../../../common/Badge';
import { useGetTenantIntegrations } from '../../../onboarding/apis/tenant-integrations';
import { sortCategories } from '../../../onboarding/registry/categoryOrder';
import {
  categoryLabel,
  registryDescription,
} from '../../../onboarding/registry/registry-text';
import { useToolsNavigationStore } from '../../../onboarding/store/toolsNavigationStore';
import { TicketingSetupCallout } from './TicketingSetupCallout';

import { useTranslation } from 'react-i18next';

export interface ToolsContentProps {
  source?: string;
  onClose?: () => void;
  onSkillSelect?: (skill: SkillConfig) => void;
  onWorkspaceSelect?: (workspace: WorkspaceConfig) => void;
}

interface ToolItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  image?: ImageProps & {
    className?: string;
    alt?: string;
  };
  type: 'skill' | 'workspace';
  status: 'available' | 'enabled' | 'hidden';
  category: string;
  featured?: boolean;
  configurable?: boolean;
  config?: SkillConfig | WorkspaceConfig;
}

interface ToolCardProps {
  item: ToolItem;
  onClick: (item: ToolItem) => void;
}

// Helper function to get category with fallback to 'Other'
const getToolCategory = (config: SkillConfig | WorkspaceConfig): string => {
  // Use the category from the registry if available
  if (config.category) {
    return config.category;
  }

  // Fallback to 'Other' if no category is specified
  return 'Other';
};

const ToolCard: React.FC<ToolCardProps> = ({ item, onClick }) => {
  const { t } = useTranslation('pleri/library');
  const getStatusBadge = () => {
    switch (item.status) {
      case 'enabled':
        return (
          <Badge
            variant="success"
            size="sm"
            className="flex items-center gap-1"
          >
            <CheckIcon className="h-3 w-3" />
            {t('active')}
          </Badge>
        );
      case 'available':
        return (
          <Badge
            variant="warning"
            size="sm"
            className="flex items-center gap-1"
          >
            <SparklesIcon className="h-3 w-3" />
            {t('configure')}
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <button
      onClick={() => onClick(item)}
      className="group relative flex h-88 w-full flex-col overflow-hidden rounded-lg border border-gray-100 bg-white p-6 text-center shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900"
    >
      {/* Row 1: Category and Status badges */}
      <div className="mb-4 flex items-start justify-between">
        <Badge variant="info" size="sm" className="text-xs">
          {categoryLabel(t, item.category)}
        </Badge>
        <div className="flex items-center gap-1" data-testid="tool-status-info">
          {getStatusBadge()}
        </div>
      </div>

      {/* Row 2: Centered Icon */}
      <div className="mb-6 flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-orange-50 text-orange-600 dark:bg-gray-200 dark:text-orange-700">
          {item.image ? (
            <div className="flex-shrink-0">
              <Image
                name={item.image.name}
                type={item.image.type}
                src={item.image.src}
                alt={item.image.alt || item.title}
                className="h-10 w-10 rounded object-contain"
                fileExtension={item.image.fileExtension}
              />
            </div>
          ) : (
            <div className="flex h-10 w-10 items-center justify-center text-orange-600">
              {item.icon}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Centered Title */}
      <div className="mb-4">
        <h3
          className="text-lg font-semibold text-gray-900 dark:text-gray-100"
          data-testid="tool-card-title"
        >
          {item.title}
        </h3>
      </div>

      {/* Row 4: Centered Description */}
      <div className="flex-1">
        <p className="overflow-hidden text-sm text-gray-500 dark:text-gray-400">
          <span
            className="block overflow-hidden text-ellipsis"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {item.description}
          </span>
        </p>
      </div>
    </button>
  );
};

export const ToolsContent: React.FC<ToolsContentProps> = ({
  source,
  onClose,
  onSkillSelect,
  onWorkspaceSelect,
}) => {
  const { t } = useTranslation('pleri/library');
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedCategory,
    scrollPosition,
    searchQuery,
    setSelectedCategory,
    setScrollPosition,
    setSearchQuery,
  } = useToolsNavigationStore();

  const [activeTab, setActiveTab] = useState<string>(selectedCategory);
  const tabListRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasRestoredScroll = useRef<boolean>(false);

  const { data: isExtensionDetected } = useBrowserExtensionDetected();
  // Check if we're on the tools route
  const isToolsRoute =
    location.pathname === '/settings/tools' ||
    location.pathname === '/settings/tools/';

  // Restore scroll position when component mounts (only once)
  useEffect(() => {
    if (
      contentRef.current &&
      scrollPosition > 0 &&
      !hasRestoredScroll.current
    ) {
      // Restore scroll position immediately without animation to prevent flickering
      contentRef.current.style.scrollBehavior = 'auto';
      contentRef.current.scrollTop = scrollPosition;
      hasRestoredScroll.current = true;

      // Re-enable smooth scrolling after restoration
      requestAnimationFrame(() => {
        if (contentRef.current) {
          contentRef.current.style.scrollBehavior = '';
        }
      });
    }
  }, [scrollPosition]);

  // Save scroll position when scrolling (with throttling to reduce flickering)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      // Clear previous timeout
      clearTimeout(timeoutId);

      // Throttle scroll position updates to reduce store updates
      timeoutId = setTimeout(() => {
        if (contentRef.current) {
          const currentScrollPosition = contentRef.current.scrollTop;
          setScrollPosition(currentScrollPosition);
        }
      }, 100); // Update every 100ms instead of every scroll event
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll, {
        passive: true,
      });
      return () => {
        contentElement.removeEventListener('scroll', handleScroll);
        clearTimeout(timeoutId);
      };
    }
  }, [setScrollPosition]);

  // Sync activeTab with store when selectedCategory changes
  useEffect(() => {
    setActiveTab(selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    // Focus the search input when component mounts
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    // Handle global keyboard events to capture typed text
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is already typing in the search input or other input fields
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Ignore special keys and modifier combinations
      if (
        event.ctrlKey ||
        event.altKey ||
        event.metaKey ||
        event.key.length > 1
      ) {
        return;
      }

      // Focus search input and set the typed character
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        setSearchQuery(searchQuery + event.key);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchQuery, setSearchQuery]);

  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetTenantIntegrations();

  const { data: userProfile } = useUserProfile();
  const userOrgId = userProfile?.organizationId || '';
  const { data: skills } = useSkills();
  const { data: workspaces } = useWorkspaces();

  const isPlerionEnabled =
    skills?.find((s) => s.skillType === 'plerion')?.enabled ?? false;

  const hasEnabledGithubIntegration =
    !isLoadingIntegrations &&
    integrations?.some(
      (integration) =>
        integration.provider?.toLowerCase() === 'github' && integration.enabled,
    );

  const hasEnabledGitlabIntegration =
    !isLoadingIntegrations &&
    integrations?.some(
      (integration) =>
        integration.provider?.toLowerCase() === 'gitlab' && integration.enabled,
    );
  const hasEnabledBitbucketIntegration =
    !isLoadingIntegrations &&
    integrations?.some(
      (integration) =>
        integration.provider?.toLowerCase() === 'bitbucket' &&
        integration.enabled,
    );

  // Convert skills and workspaces to unified ToolItem format
  const toolsItems: ToolItem[] = useMemo(() => {
    const isAlwaysEnabledForOrg = (skillConfig: SkillConfig) => {
      if (!Array.isArray(skillConfig.alwaysEnabledForOrgs)) {
        return false;
      }

      return (
        skillConfig.alwaysEnabledForOrgs.includes('*') ||
        skillConfig.alwaysEnabledForOrgs.includes(userOrgId ?? '')
      );
    };

    const isGithubSkillEnabled = (skillConfig: SkillConfig) => {
      return (
        skillConfig.id === 'github' &&
        hasEnabledGithubIntegration &&
        isPlerionEnabled
      );
    };

    const isGitlabSkillEnabled = (skillConfig: SkillConfig) => {
      return (
        skillConfig.id === 'gitlab' &&
        hasEnabledGitlabIntegration &&
        isPlerionEnabled
      );
    };

    const isBitbucketSkillEnabled = (skillConfig: SkillConfig) => {
      return (
        skillConfig.id === 'bitbucket' &&
        hasEnabledBitbucketIntegration &&
        isPlerionEnabled
      );
    };

    const isEnabledSkill = (skillConfig: SkillConfig, skillState?: Skill) => {
      // Check if skill is always enabled for this organization
      if (isAlwaysEnabledForOrg(skillConfig)) {
        return true;
      }

      // Check if GitHub skill has special conditions
      if (isGithubSkillEnabled(skillConfig)) {
        return true;
      }

      // Check if GitLab skill has special conditions
      if (isGitlabSkillEnabled(skillConfig)) {
        return true;
      }

      // Check if Bitbucket skill has special conditions
      if (isBitbucketSkillEnabled(skillConfig)) {
        return true;
      }
      // Default to skill state enabled status
      return skillState?.enabled ?? false;
    };

    const items: ToolItem[] = [];

    // Add skills
    const uiSkills = skillRegistry.getAllSkills();
    uiSkills.forEach((skillConfig) => {
      const skillState = skills?.find((s) => s.skillType === skillConfig.id);
      const isEnabled = isEnabledSkill(skillConfig, skillState);

      let status: 'enabled' | 'available' | 'hidden';
      if (isEnabled) {
        status = 'enabled';
      } else if (skillConfig.status === 'hidden') {
        status = 'hidden';
      } else {
        status = 'available';
      }

      // Use category from registry
      const category = getToolCategory(skillConfig);

      items.push({
        id: `skill-${skillConfig.id}`,
        title: skillConfig.name,
        description: registryDescription(
          t,
          'skill',
          skillConfig.id,
          skillConfig.description,
        ),
        icon: skillConfig.icon,
        image: skillConfig.image,
        type: 'skill' as const,
        status,
        category,
        featured: isEnabled,
        configurable: skillConfig.configurable,
        config: skillConfig,
      });
    });

    // Add workspaces
    const uiWorkspaces = workspaceRegistry.getAllWorkspaces();
    uiWorkspaces.forEach((workspaceConfig) => {
      const workspaceState = workspaces?.find(
        (w) => w.workspaceType === workspaceConfig.id,
      );
      const isEnabled = workspaceState?.enabled ?? false;
      let status: 'enabled' | 'available' | 'hidden';
      if (isEnabled) {
        status = 'enabled';
      } else if (workspaceConfig.status === 'hidden') {
        status = 'hidden';
      } else {
        status = 'available';
      }
      if (workspaceConfig.id === WorkspaceType.BROWSER) {
        status = isExtensionDetected === true ? 'enabled' : 'available';
      }

      // Use category from registry
      const category = getToolCategory(workspaceConfig);

      items.push({
        id: `workspace-${workspaceConfig.id}`,
        title: workspaceConfig.name,
        description: registryDescription(
          t,
          'workspace',
          workspaceConfig.id,
          workspaceConfig.description,
        ),
        icon: workspaceConfig.icon,
        image: workspaceConfig.image,
        type: 'workspace' as const,
        status,
        category,
        featured: isEnabled,
        configurable: workspaceConfig.configurable,
        config: {
          ...workspaceConfig,
          enabled: isEnabled,
          config: workspaceState?.config,
        },
      });
    });

    // Sort items: enabled first, then available, then hidden
    return items.sort((a, b) => {
      const statusOrder = { enabled: 0, available: 1, hidden: 2 };
      return statusOrder[a.status] - statusOrder[b.status];
    });
  }, [
    skills,
    workspaces,
    userOrgId,
    hasEnabledGithubIntegration,
    hasEnabledGitlabIntegration,
    hasEnabledBitbucketIntegration,
    isPlerionEnabled,
    isExtensionDetected,
    t,
  ]);

  // Filter out hidden items so they don't appear in the UI
  const visibleToolsItems = toolsItems.filter(
    (item) => item.status !== 'hidden',
  );

  // Generate categories from available tools
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    visibleToolsItems.forEach((item) => {
      if (item.category) {
        categorySet.add(item.category);
      }
    });

    // Always include 'All' as the first tab, then sort other categories according to defined order
    const otherCategories = Array.from(categorySet).filter(
      (cat) => cat !== 'All',
    );
    const sortedCategories = sortCategories(otherCategories);

    // Only add 'Other' if there are tools that don't fit other categories
    const finalCategories = ['All', ...sortedCategories];
    if (categorySet.has('Other')) {
      finalCategories.push('Other');
    }

    return finalCategories;
  }, [visibleToolsItems]);

  // Filter tools based on active tab and search query
  const filteredTools = useMemo(() => {
    let filtered = visibleToolsItems;

    // Filter by category
    if (activeTab !== 'All') {
      filtered = filtered.filter((item) => item.category === activeTab);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.description.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    return filtered;
  }, [visibleToolsItems, activeTab, searchQuery]);

  const handleItemClick = (item: ToolItem) => {
    // Don't proceed if item has no config
    if (!item.config) {
      return;
    }

    if (item.type === 'skill' && onSkillSelect && item.config) {
      onSkillSelect(item.config as SkillConfig);
    } else if (item.type === 'workspace' && onWorkspaceSelect && item.config) {
      onWorkspaceSelect(item.config as WorkspaceConfig);
    }

    if (onClose) {
      onClose();
    }
  };

  // Handle tab selection
  const handleTabSelect = (category: string) => {
    setActiveTab(category);
    setSelectedCategory(category);

    // Update URL if we're on the tools route
    if (isToolsRoute) {
      const search = {
        ...(category !== 'All' ? { category } : {}),
        ...(source ? { source } : {}),
      };
      navigate({
        to: '/settings/tools',
        search,
        replace: true, // Replace history entry to avoid cluttering browser history
      });
    }
  };

  const isCreateTicketRedirect = source === 'create-ticket-cta';
  const shouldShowTicketingSetupAlert =
    isCreateTicketRedirect && activeTab === 'Ticketing';

  useEffect(() => {
    if (isCreateTicketRedirect && searchQuery) {
      setSearchQuery('');
    }
  }, [isCreateTicketRedirect, searchQuery, setSearchQuery]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white dark:bg-gray-900">
      {/* Header with Search */}
      <div className="border-b border-gray-100 px-6 py-4 dark:border-gray-800">
        {!isCreateTicketRedirect && (
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-xl leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100">
              {t('tools')}
            </h1>
            <p className="mt-1.5 text-sm font-medium text-gray-600 dark:text-gray-400">
              {t('giveMeAccessToTheToolsWe')}
            </p>
          </div>
        )}

        {shouldShowTicketingSetupAlert && (
          <div className="mx-auto mt-3 max-w-3xl">
            <TicketingSetupCallout />
          </div>
        )}

        {!isCreateTicketRedirect && (
          <div className="mx-auto mt-3 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder={t('searchTools')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pr-4 pl-10 text-sm font-medium focus:border-orange-300 focus:ring-2 focus:ring-orange-200 focus:outline-none dark:border-gray-700 dark:bg-gray-900"
                ref={searchInputRef}
                data-testid="search-tools-input"
              />
            </div>
          </div>
        )}
      </div>
      {/* Horizontal Tab Navigation */}
      <div className="sticky top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95">
        <div className="mx-auto max-w-7xl px-6">
          <div
            ref={tabListRef}
            className="scrollbar-hide flex overflow-x-auto py-2"
            role="tablist"
            aria-label={t('toolCategories')}
          >
            {categories.map((category) => (
              <button
                key={category}
                role="tab"
                aria-selected={activeTab === category}
                aria-controls={`panel-${category}`}
                id={`tab-${category}`}
                onClick={() => handleTabSelect(category)}
                className={`flex-shrink-0 border-b-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none ${
                  activeTab === category
                    ? 'border-b-2 border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                }`}
              >
                {categoryLabel(t, category)}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Content Area */}
      <div className="flex-1 overflow-y-auto" ref={contentRef}>
        <div className="mx-auto max-w-7xl px-6 pt-6 pb-[200px]">
          {/* Tab Panel */}
          <div
            role="tabpanel"
            id={`panel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
            className="min-h-[400px]"
          >
            {/* Results count */}
            {searchQuery && (
              <div className="mb-6">
                <p className="text-sm text-orange-600">
                  {t('resultsFound', { count: filteredTools.length })}
                </p>
              </div>
            )}

            {/* Tools Grid */}
            {filteredTools.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {filteredTools.map((item) => (
                  <ToolCard
                    key={item.id}
                    item={item}
                    onClick={handleItemClick}
                  />
                ))}
              </div>
            ) : (
              <div className="flex h-64 items-center justify-center">
                <div className="text-center">
                  <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-300" />
                  <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {searchQuery
                      ? t('noResultsFound')
                      : t('noToolsInThisCategory')}
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {searchQuery
                      ? t('tryAdjustingYourSearchTermsOrBrowseCategories')
                      : t('toolsWillAppearHereOnceTheyAre')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
